import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  ExternalLink,
  Home,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { getApiErrorMessage } from '../utils/errorMessage';

const statusConfig = {
  payment_pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Payment Pending' },
  processing: { icon: Package, color: 'bg-blue-100 text-blue-800', label: 'Processing' },
  shipped: { icon: Truck, color: 'bg-purple-100 text-purple-800', label: 'Shipped' },
  in_transit: { icon: Truck, color: 'bg-indigo-100 text-indigo-800', label: 'In Transit' },
  out_for_delivery: { icon: Truck, color: 'bg-indigo-100 text-indigo-800', label: 'Out for Delivery' },
  delivered: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Delivered' },
  cancelled: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Cancelled' },
  payment_failed: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Payment Failed' },
};

const getProgressStep = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'delivered') return 3;
  if (['shipped', 'in_transit', 'out_for_delivery'].includes(normalized)) return 2;
  if (normalized === 'processing') return 1;
  return 0;
};

const progressNodeClass = (isActive, isDone) =>
  isDone || isActive
    ? 'bg-green-600 text-white'
    : 'bg-amber-200 text-amber-700';

const progressLineClass = (isDone) => (isDone ? 'bg-green-200' : 'bg-amber-200');

const OrderTracking = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderNumber) {
        navigate('/my-orders');
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/orders/track/${orderNumber}`);
        setOrder(response.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to load tracking details'));
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderNumber, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-4 border-amber-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-amber-700">Loading order tracking...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-amber-800 mb-4">Order not found.</p>
          <Button onClick={() => navigate('/my-orders')} className="bg-amber-600 hover:bg-amber-700 text-white">
            Go to My Orders
          </Button>
        </div>
      </div>
    );
  }

  const currentStatus = order?.tracking?.status || 'payment_pending';
  const currentConfig = statusConfig[currentStatus] || statusConfig.payment_pending;
  const CurrentStatusIcon = currentConfig.icon;
  const history = order?.tracking?.history || [];
  const progressStep = getProgressStep(currentStatus);
  const hasTerminalFailure = ['cancelled', 'payment_failed'].includes(String(currentStatus || '').toLowerCase());

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm text-amber-700">Order Number</p>
              <h1 className="text-2xl font-bold text-amber-950 font-mono">{order.order_number}</h1>
            </div>
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${currentConfig.color}`}>
              <CurrentStatusIcon className="h-4 w-4" />
              {order?.tracking?.status_label || currentConfig.label}
            </span>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-5 w-5 text-amber-600" />
              <h3 className="font-semibold text-amber-950">Order Status</h3>
            </div>

            {hasTerminalFailure ? (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">
                This order is currently marked as{' '}
                <span className="font-semibold">
                  {order?.tracking?.status_label || currentConfig.label}
                </span>
                .
              </div>
            ) : (
              <div className="relative overflow-x-auto">
                <div className="min-w-[640px] flex items-center justify-between">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${progressNodeClass(
                        progressStep === 0,
                        progressStep > 0
                      )}`}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </div>
                    <span className={`text-xs mt-1 ${progressStep >= 0 ? 'text-amber-900 font-medium' : 'text-amber-600'}`}>
                      Confirmed
                    </span>
                  </div>

                  <div className={`flex-1 h-1 ${progressLineClass(progressStep > 0)}`}></div>

                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${progressNodeClass(
                        progressStep === 1,
                        progressStep > 1
                      )}`}
                    >
                      <Package className="h-4 w-4" />
                    </div>
                    <span className={`text-xs mt-1 ${progressStep >= 1 ? 'text-amber-900 font-medium' : 'text-amber-600'}`}>
                      Processing
                    </span>
                  </div>

                  <div className={`flex-1 h-1 ${progressLineClass(progressStep > 1)}`}></div>

                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${progressNodeClass(
                        progressStep === 2,
                        progressStep > 2
                      )}`}
                    >
                      <Truck className="h-4 w-4" />
                    </div>
                    <span className={`text-xs mt-1 ${progressStep >= 2 ? 'text-amber-900 font-medium' : 'text-amber-600'}`}>
                      Shipped
                    </span>
                  </div>

                  <div className={`flex-1 h-1 ${progressLineClass(progressStep > 2)}`}></div>

                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${progressNodeClass(
                        progressStep === 3,
                        false
                      )}`}
                    >
                      <Home className="h-4 w-4" />
                    </div>
                    <span className={`text-xs mt-1 ${progressStep >= 3 ? 'text-amber-900 font-medium' : 'text-amber-600'}`}>
                      Delivered
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {order?.tracking?.tracking_number && (
            <div className="mt-4 p-4 bg-amber-50 rounded-xl">
              <p className="text-sm font-medium text-amber-900">Shipment</p>
              <p className="text-sm text-amber-700 mt-1">
                {order?.tracking?.courier_name || 'Courier'}: <span className="font-mono">{order.tracking.tracking_number}</span>
              </p>
              {order?.tracking?.estimated_delivery && (
                <p className="text-sm text-amber-700">
                  Estimated Delivery: {new Date(order.tracking.estimated_delivery).toLocaleDateString('en-IN')}
                </p>
              )}
              {order?.tracking?.tracking_url && (
                <a
                  href={order.tracking.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-amber-700 hover:text-amber-900 underline mt-2"
                >
                  Open tracking link
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border-2 border-amber-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-amber-950 mb-4">Order Timeline</h2>
            <div className="space-y-4">
              {history.length === 0 ? (
                <p className="text-sm text-amber-700">No timeline events yet.</p>
              ) : (
                history
                  .slice()
                  .reverse()
                  .map((entry, index) => (
                    <div key={`${entry.timestamp}-${index}`} className="border border-amber-100 rounded-xl p-3">
                      <p className="text-sm font-semibold text-amber-900">{entry.status?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-amber-700 mt-1">
                        {new Date(entry.timestamp).toLocaleString('en-IN')}
                      </p>
                      {entry.note && <p className="text-sm text-amber-800 mt-2">{entry.note}</p>}
                    </div>
                  ))
              )}
            </div>
          </div>

          <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 shadow-sm space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-amber-950 mb-2">Price Breakdown</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-amber-700">
                  <span>Subtotal</span>
                  <span>Rs {Number(order?.pricing_breakdown?.subtotal ?? 0).toFixed(2)}</span>
                </div>
                {Number(order?.pricing_breakdown?.discount ?? 0) > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>Discount</span>
                    <span>-Rs {Number(order?.pricing_breakdown?.discount ?? 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-amber-700">
                  <span>Shipping</span>
                  <span>
                    {Number(order?.pricing_breakdown?.shipping_fee ?? 0) === 0
                      ? 'FREE'
                      : `Rs ${Number(order?.pricing_breakdown?.shipping_fee ?? 0).toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between font-semibold text-amber-950 border-t border-amber-100 pt-2">
                  <span>Total</span>
                  <span>Rs {Number(order?.pricing_breakdown?.total ?? 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-amber-950 mb-2">Delivery Address</h2>
              <div className="flex items-start gap-2 text-sm text-amber-800">
                <MapPin className="h-4 w-4 mt-0.5 text-amber-600" />
                <div>
                  <p className="font-medium">{order?.shipping_address?.name}</p>
                  <p>{order?.shipping_address?.address_line1}</p>
                  {order?.shipping_address?.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                  <p>
                    {order?.shipping_address?.city}, {order?.shipping_address?.state} - {order?.shipping_address?.pincode}
                  </p>
                </div>
              </div>
            </div>

            <Button onClick={() => navigate('/my-orders')} variant="outline" className="w-full border-amber-300 text-amber-700">
              Back to My Orders
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;
